import { Component, type ErrorInfo, type ReactNode } from "react"

// Route-level boundary so a single render/hook error can't white-screen the app.

interface Props {
  /** A node, or a function given the error so the fallback can describe it. */
  fallback?: ReactNode | ((error: Error) => ReactNode)
  children: ReactNode
  onError?: (error: Error, info: ErrorInfo) => void
}

interface State {
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info)
    this.props.onError?.(error, info)
  }

  override render(): ReactNode {
    if (this.state.error) {
      if (typeof this.props.fallback === "function") {
        return this.props.fallback(this.state.error)
      }
      return (
        this.props.fallback ?? (
          <div role="alert" className="mx-auto max-w-lg p-8">
            <h1 className="text-lg font-semibold">Something went wrong.</h1>
            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
              {this.state.error.message}
            </p>
            <button
              type="button"
              className="mt-4 rounded border px-3 py-1 text-sm"
              onClick={() => {
                this.setState({ error: null })
              }}
            >
              Try again
            </button>
          </div>
        )
      )
    }
    return this.props.children
  }
}
